import * as React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import { Button, Collapse, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import DragAndDropFile from "../Account/drag-and-drop";
import { useUserProfileSelector } from "../../hooks/useUserProfile";
import { getAuthGlassFieldSx, getAuthPrimaryButtonSx } from "../Auth/authGlassStyles";

/**
 * Shared name / email / logo / disclaimer fields for account editing (embedded or standalone).
 */
export default function AccountProfileForm({
  formik,
  setLogoId,
  load,
  onCancel: _onCancel,
  hideLogoSection = false,
}) {
  const theme = useTheme();
  const { userProfile: profile } = useUserProfileSelector();
  const fieldSx = getAuthGlassFieldSx(theme);
  const [showMore, setShowMore] = React.useState(false);

  return (
    <Stack
      gap={2}
      mt={hideLogoSection ? 1.25 : 2}
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <TextField
        required
        label={formik.touched.name && formik.errors.name ? formik.errors.name : "Name"}
        fullWidth
        variant="outlined"
        id="name"
        type="text"
        inputMode="text"
        autoComplete="name"
        value={formik.values.name}
        name="name"
        onChange={formik.handleChange}
        error={formik.touched.name && Boolean(formik.errors.name)}
        onBlur={formik.handleBlur}
        InputLabelProps={
          formik.touched.name && formik.errors.name ? { shrink: true } : undefined
        }
        sx={fieldSx}
      />

      <TextField
        label="Email"
        fullWidth
        variant="outlined"
        id="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={formik.values.email}
        name="email"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <LockOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
        sx={[
          fieldSx,
          {
            "& .MuiOutlinedInput-root": {
              pointerEvents: "none",
              "& fieldset": { borderColor: "rgba(0,0,0,0.10) !important" },
            },
            "& .MuiOutlinedInput-input": {
              WebkitTextFillColor: "rgba(0,0,0,0.40) !important",
              color: "rgba(0,0,0,0.40) !important",
            },
            "& .MuiInputLabel-root": {
              color: "rgba(0,0,0,0.38) !important",
            },
          },
        ]}
      />

      {!hideLogoSection ? (
        <Box sx={{ maxWidth: 280, width: "100%", mx: "auto" }}>
          <DragAndDropFile
            logo={profile?.logo}
            preventCall={true}
            setLogoId={setLogoId}
          />
        </Box>
      ) : null}

      <Box>
        <Button
          variant="text"
          size="small"
          onClick={() => setShowMore((v) => !v)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: "18px !important",
                transform: showMore ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          }
          sx={{
            textTransform: "none !important",
            color: "text.secondary",
            fontSize: "0.8125rem",
            fontWeight: 500,
            py: "4px",
            px: "6px",
            minHeight: 0,
            letterSpacing: 0,
            "&:hover": { bgcolor: "transparent", color: "text.primary" },
          }}
          disableRipple
        >
          More
        </Button>
        <Collapse in={showMore}>
          <Box pt={1.5}>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              label="Disclaimer"
              id="disclaimer"
              inputMode="text"
              value={formik.values.disclaimer}
              placeholder="Optional: Add a disclaimer to your exported images"
              name="disclaimer"
              onChange={formik.handleChange}
              error={formik.touched.disclaimer && Boolean(formik.errors.disclaimer)}
              helperText={formik.touched.disclaimer && formik.errors.disclaimer}
              onBlur={formik.handleBlur}
              sx={fieldSx}
            />
          </Box>
        </Collapse>
      </Box>

      <Box mt={1}>
        <Stack sx={{ "& .MuiButton-root": { minHeight: 44 } }}>
          <LoadingButton
            loading={load}
            variant="contained"
            fullWidth
            type="submit"
            sx={getAuthPrimaryButtonSx(theme)}
          >
            Save changes
          </LoadingButton>
        </Stack>
      </Box>
    </Stack>
  );
}

AccountProfileForm.propTypes = {
  formik: PropTypes.object.isRequired,
  setLogoId: PropTypes.func.isRequired,
  load: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  hideLogoSection: PropTypes.bool,
};
